import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { environment } from '../../environments/environment';

export interface EntraUserSyncStatus {
  state: 'Idle' | 'Running' | 'Completed' | 'Failed';
  groupsTotal: number;
  groupsProcessed: number;
  currentActivity: string;
  startedAt: string | null;
  completedAt: string | null;
  lastResult: EntraUserSyncResult | null;
}

export interface EntraUserSyncJobQueueStatus {
  isQueuedOrRunning: boolean;
  queuedAtUtc: string | null;
  dequeuedAtUtc: string | null;
  completedAtUtc: string | null;
}

export interface EntraUserSyncResult {
  success: boolean;
  errorMessage: string | null;
  groupsTotal: number;
  groupsProcessed: number;
  entraUsersDistinct: number;
  inserted: number;
  updated: number;
  deactivated: number;
  reactivated: number;
  unchanged: number;
  errors: string[];
  duration: string;
  startedAt: string;
  completedAt: string | null;
}

export interface EntraGraphTestResponse {
  delegated: boolean;
  group: string;
  firstPageOnly: boolean;
  topResults: number;
  membersReturned: number;
  sample: Array<{
    objectId: string | null;
    userPrincipalName: string | null;
    displayName: string | null;
    mail: string | null;
    givenName: string | null;
    surname: string | null;
  }>;
}

export interface RoleSyncConfig {
  staleThresholdHours: number;
  updatedAtUtc: string | null;
}

export interface UpdateRoleSyncConfig {
  staleThresholdHours: number;
}

@Injectable({
  providedIn: 'root'
})
export class EntraUserSyncService {
  private apiUrl: string;

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiUrl + '/admin/users/entra-sync';
  }

  /**
   * Get the current sync status
   */
  getSyncStatus(): Observable<EntraUserSyncStatus> {
    return this.http.get<EntraUserSyncStatus>(`${this.apiUrl}/status`);
  }

  /**
   * Get the current background job queue status (queued/running).
   */
  getJobStatus(): Observable<EntraUserSyncJobQueueStatus> {
    return this.http.get<EntraUserSyncJobQueueStatus>(`${this.apiUrl}/job-status`);
  }

  /**
   * Get sync status and background job queue status together.
   */
  getStatusWithJob(): Observable<{ status: EntraUserSyncStatus; job: EntraUserSyncJobQueueStatus }> {
    return forkJoin({
      status: this.getSyncStatus(),
      job: this.getJobStatus()
    });
  }

  /**
   * Trigger a manual sync
   */
  triggerSync(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/trigger`, {});
  }

  /**
   * Get role sync staleness threshold config (DB-backed)
   */
  getRoleSyncConfig(): Observable<RoleSyncConfig> {
    return this.http.get<RoleSyncConfig>(`${this.apiUrl}/config`);
  }

  /**
   * Update role sync staleness threshold config (DB-backed)
   */
  updateRoleSyncConfig(staleThresholdHours: number): Observable<RoleSyncConfig> {
    const payload: UpdateRoleSyncConfig = { staleThresholdHours };
    return this.http.put<RoleSyncConfig>(`${this.apiUrl}/config`, payload);
  }

  /**
   * Test CMID Graph connection and return sample members.
   */
  testGraph(group: string, delegated = true, firstPageOnly = true, topResults = 50): Observable<EntraGraphTestResponse> {
    return this.http.get<EntraGraphTestResponse>(`${this.apiUrl}/test-graph`, {
      params: {
        group,
        delegated: String(delegated),
        firstPageOnly: String(firstPageOnly),
        topResults: String(topResults)
      }
    });
  }
}
